const CAJA_KEY_SEP = '\u001f';

function normalizeSucursalIdToken(id) {
  return String(id ?? '')
    .trim()
    .toLowerCase()
    .replace(/[{}]/g, '');
}

function normalizeCajaRows(catalogoList = []) {
  return catalogoList
    .map((c) => ({
      codigo: Number(c.codigo ?? c.Codigo),
      nombre: String(c.nombre ?? c.Nombre ?? '').trim() || `Caja ${c.codigo ?? c.Codigo}`,
      sucursalNombre: String(c.sucursalNombre ?? '').trim(),
      sucursalId: String(c.sucursalId ?? c.SucursalID ?? '').trim(),
    }))
    .filter((c) => Number.isFinite(c.codigo));
}

export function buildCajaItemKey({ sucursalId, sucursalNombre, codigo, nombre }) {
  const sid = String(sucursalId || sucursalNombre || 'suc');
  return [sid, String(codigo), String(nombre ?? '')].join(CAJA_KEY_SEP);
}

/** Clave generalizada por número de caja (aplica a todas las sucursales en alcance). */
export function buildCajaCodigoKey(codigo) {
  return String(codigo);
}

export function parseCajaItemKey(key) {
  const keyStr = String(key);
  if (!keyStr.includes(CAJA_KEY_SEP)) {
    const codigo = Number(keyStr.trim());
    return { sucursalId: '', codigo: Number.isFinite(codigo) ? codigo : NaN, nombre: '' };
  }
  const parts = keyStr.split(CAJA_KEY_SEP);
  const sucursalId = parts[0] ?? '';
  const codigo = Number(parts[1]);
  const nombre = parts.slice(2).join(CAJA_KEY_SEP);
  return { sucursalId, codigo, nombre };
}

export function codigosFromCajaKeys(keys = []) {
  return [...new Set(keys.map((k) => parseCajaItemKey(k).codigo).filter(Number.isFinite))].sort(
    (a, b) => a - b,
  );
}

function isContiguous(sortedCodigos = []) {
  return sortedCodigos.every((c, i) => i === 0 || c === sortedCodigos[i - 1] + 1);
}

/**
 * Limita el catálogo a las sucursales elegidas en el filtro de sucursales.
 * Con "Todas" devuelve el catálogo completo (todas las sucursales).
 */
export function filterCajasCatalogoPorSucursales(
  catalogoList = [],
  { sucursalesTodas = true, sucursalesSeleccionadas = [], sucursales = [] } = {},
) {
  if (sucursalesTodas || sucursalesSeleccionadas.length === 0) {
    return catalogoList;
  }
  const selectedIds = new Set(sucursalesSeleccionadas.map((id) => normalizeSucursalIdToken(id)));
  const selectedBranches = sucursales.filter((s) =>
    selectedIds.has(normalizeSucursalIdToken(s.sucursalId)),
  );
  const selectedNames = new Set(
    selectedBranches
      .map((s) => String(s.nombreSucursal ?? '').trim().toLowerCase())
      .filter(Boolean),
  );
  const knownCatalogIds = new Set(
    sucursales
      .map((s) => normalizeSucursalIdToken(s.sucursalId))
      .filter(Boolean),
  );

  return catalogoList.filter((c) => {
    const rawSid = String(c.sucursalId ?? c.SucursalID ?? '').trim();
    const sid = normalizeSucursalIdToken(rawSid);
    if (sid && selectedIds.has(sid)) return true;
    if (sid && knownCatalogIds.has(sid)) {
      const branch = sucursales.find(
        (s) => normalizeSucursalIdToken(s.sucursalId) === sid,
      );
      if (branch && selectedIds.has(normalizeSucursalIdToken(branch.sucursalId))) {
        return true;
      }
    }
    const name = String(c.sucursalNombre ?? '').trim().toLowerCase();
    return Boolean(name && selectedNames.has(name));
  });
}

function isDefaultCajaNombre(codigo, nombre) {
  const n = String(nombre ?? '').trim().toLowerCase();
  return !n || n === `caja ${codigo}` || n === String(codigo);
}

function uniqueCajaNombresParaGrupo(rows = [], codigo) {
  const nombres = rows
    .map((r) => String(r.nombre ?? '').trim())
    .filter((n) => n && !isDefaultCajaNombre(codigo, n));
  return [...new Set(nombres)];
}

/** Etiqueta de caja agrupada por número; un solo nombre si coincide, si no concatena los distintos. */
export function buildCajaGrupoLabel(codigo, rows = []) {
  const nombres = uniqueCajaNombresParaGrupo(rows, codigo);
  if (nombres.length === 0) return `Caja ${codigo}`;
  if (nombres.length === 1) return `Caja ${codigo} ${nombres[0]}`;
  return `Caja ${codigo} ${nombres.join(' · ')}`;
}

function groupCajaRowsByCodigo(rows = []) {
  const map = new Map();
  for (const row of rows) {
    if (!map.has(row.codigo)) map.set(row.codigo, []);
    map.get(row.codigo).push(row);
  }
  return [...map.entries()].sort((a, b) => a[0] - b[0]);
}

/** Etiqueta de una fila de caja (catálogo sin agrupar). */
export function buildCajaFilaLabel(row, { mostrarSucursal = false } = {}) {
  const codigo = row.codigo;
  const nombre = String(row.nombre ?? '').trim();
  const suc = String(row.sucursalNombre ?? '').trim();
  const base =
    nombre && !isDefaultCajaNombre(codigo, nombre) ? `Caja ${codigo} ${nombre}` : `Caja ${codigo}`;
  if (mostrarSucursal && suc) return `${base} (${suc})`;
  return base;
}

function sucursalesFiltroEspecifico(sucursalesTodas, sucursalesSeleccionadas = []) {
  return !sucursalesTodas && sucursalesSeleccionadas.length > 0;
}

/**
 * Lista de cajas para el filtro.
 * - Todas las sucursales: una opción por número de caja (agrupado).
 * - Sucursal(es) concretas: una opción por fila del catálogo (sin colapsar duplicados).
 */
export function buildCajasAgrupadas(
  catalogoCompleto = [],
  catalogoVisibilidad = null,
  { sucursalesTodas = true, sucursalesSeleccionadas = [] } = {},
) {
  const visRows = normalizeCajaRows(catalogoVisibilidad ?? catalogoCompleto);
  if (visRows.length === 0) return [];

  if (sucursalesFiltroEspecifico(sucursalesTodas, sucursalesSeleccionadas)) {
    const mostrarSucursal = sucursalesSeleccionadas.length > 1;
    return visRows
      .slice()
      .sort(
        (a, b) =>
          a.codigo - b.codigo ||
          String(a.sucursalNombre ?? '').localeCompare(String(b.sucursalNombre ?? ''), 'es') ||
          String(a.nombre ?? '').localeCompare(String(b.nombre ?? ''), 'es'),
      )
      .map((row) => ({
        codigo: row.codigo,
        key: buildCajaItemKey(row),
        label: buildCajaFilaLabel(row, { mostrarSucursal }),
        sucursalId: row.sucursalId,
        sucursalNombre: row.sucursalNombre,
      }));
  }

  return groupCajaRowsByCodigo(visRows).map(([codigo, rows]) => ({
    codigo,
    key: buildCajaCodigoKey(codigo),
    label: buildCajaGrupoLabel(codigo, rows),
  }));
}

export function resolveSucursalFiltro({
  sucursalesTodas,
  sucursalesSeleccionadas = [],
  sucursales = [],
}) {
  if (sucursalesTodas || sucursalesSeleccionadas.length === 0) {
    const allIds = sucursales
      .map((s) => String(s.sucursalId ?? '').trim())
      .filter(Boolean);
    if (allIds.length === 0) {
      return { allBranches: true, sucursalId: '', sucursalIds: [] };
    }
    if (allIds.length === 1) {
      return { allBranches: false, sucursalId: allIds[0], sucursalIds: [] };
    }
    return { allBranches: false, sucursalId: '', sucursalIds: allIds };
  }
  const ids = sucursalesSeleccionadas.map(String);
  if (ids.length === 1) {
    return { allBranches: false, sucursalId: ids[0], sucursalIds: [] };
  }
  return { allBranches: false, sucursalId: '', sucursalIds: ids };
}

function tieneScopeSucursales(sucursalesTodas = true, sucursalesSeleccionadas = []) {
  return sucursalesTodas || sucursalesSeleccionadas.length > 0;
}

/** "TODAS" las cajas dentro del alcance de sucursales activo (todas las empresa o las elegidas). */
export function cajasTodasEnScope(
  cajasTodas = true,
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
) {
  return cajasTodas && tieneScopeSucursales(sucursalesTodas, sucursalesSeleccionadas);
}

/** Indica si el formulario ya tiene un filtro de cajas válido para consultar. */
export function reporteTieneFiltroCajasSuficiente({
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
  cajasTodas = true,
  cajasSeleccionadas = [],
}) {
  if (sucursalesTodas) return true;
  if (sucursalesSeleccionadas.length === 0) return true;
  if (cajasTodasEnScope(cajasTodas, sucursalesTodas, sucursalesSeleccionadas)) return true;
  return cajasSeleccionadas.length > 0;
}

function resolveSucursalIdDesdeParte(part, sucursales = []) {
  const token = String(part ?? '').trim();
  if (!token) return '';
  const tokenNorm = normalizeSucursalIdToken(token);
  const byId = sucursales.find(
    (s) => normalizeSucursalIdToken(s.sucursalId) === tokenNorm,
  );
  if (byId) return String(byId.sucursalId);
  const tokenLower = token.toLowerCase();
  const byName = sucursales.find(
    (s) => String(s.nombreSucursal ?? '').trim().toLowerCase() === tokenLower,
  );
  return byName ? String(byName.sucursalId) : token;
}

/** Resuelve IDs de sucursal a partir de las claves únicas de caja seleccionadas. */
export function extractSucursalIdsFromCajaKeys(
  cajaKeys = [],
  sucursales = [],
  cajasCatalogo = [],
) {
  const ids = new Set();
  const catalogNorm = normalizeCajaRows(cajasCatalogo);

  for (const key of cajaKeys) {
    const keyStr = String(key);

    if (!keyStr.includes(CAJA_KEY_SEP)) {
      const codigo = Number(keyStr.trim());
      if (Number.isFinite(codigo)) {
        for (const c of catalogNorm) {
          if (c.codigo !== codigo) continue;
          const resolved = resolveSucursalIdDesdeParte(
            c.sucursalId || c.sucursalNombre,
            sucursales,
          );
          if (resolved) ids.add(resolved);
        }
        continue;
      }
    }

    const fromCatalog = catalogNorm.find((c) => buildCajaItemKey(c) === keyStr);
    if (fromCatalog) {
      const resolved = resolveSucursalIdDesdeParte(
        fromCatalog.sucursalId || fromCatalog.sucursalNombre,
        sucursales,
      );
      if (resolved) ids.add(resolved);
      continue;
    }
    const { sucursalId: keyPart } = parseCajaItemKey(keyStr);
    const resolved = resolveSucursalIdDesdeParte(keyPart, sucursales);
    if (resolved) ids.add(resolved);
  }
  return [...ids];
}

function cajasCatalogoEnAlcanceSucursales(
  cajasCatalogo = [],
  { sucursalesTodas = true, sucursalesSeleccionadas = [], sucursales = [] } = {},
) {
  return filterCajasCatalogoPorSucursales(cajasCatalogo, {
    sucursalesTodas,
    sucursalesSeleccionadas,
    sucursales,
  });
}

/**
 * Alcance de sucursales efectivo para el API.
 * Las sucursales elegidas en el filtro tienen prioridad; las cajas solo acotan datos dentro de ese alcance.
 */
export function resolveSucursalFiltroEfectivo({
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
  cajasTodas = true,
  cajasSeleccionadas = [],
  sucursales = [],
  cajasCatalogo = [],
}) {
  const base = resolveSucursalFiltro({ sucursalesTodas, sucursalesSeleccionadas, sucursales });
  const todasEnScope = cajasTodasEnScope(cajasTodas, sucursalesTodas, sucursalesSeleccionadas);

  if (todasEnScope) {
    return base;
  }

  if (cajasSeleccionadas.length === 0) {
    if (!sucursalesTodas && sucursalesSeleccionadas.length > 0) {
      return { ...base, sinCajasParaSucursal: true };
    }
    return base;
  }

  // Sucursales ya elegidas: la selección de caja no amplía el alcance de sucursales.
  if (!sucursalesTodas && sucursalesSeleccionadas.length > 0) {
    return base;
  }

  const catalogoEnScope = cajasCatalogoEnAlcanceSucursales(cajasCatalogo, {
    sucursalesTodas,
    sucursalesSeleccionadas,
    sucursales,
  });
  const idsFromCajas = extractSucursalIdsFromCajaKeys(
    cajasSeleccionadas,
    sucursales,
    catalogoEnScope,
  );
  if (idsFromCajas.length === 0) {
    return base;
  }

  if (idsFromCajas.length === 1) {
    return { allBranches: false, sucursalId: idsFromCajas[0], sucursalIds: [] };
  }
  return { allBranches: false, sucursalId: '', sucursalIds: idsFromCajas };
}

export function resolveCajaFiltro({
  cajasTodas,
  cajasSeleccionadas = [],
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
}) {
  const todasEnScope = cajasTodasEnScope(cajasTodas, sucursalesTodas, sucursalesSeleccionadas);

  if (todasEnScope) {
    return { cajaInicio: null, cajaFin: null, cajasCodigos: [] };
  }

  if (
    !sucursalesTodas &&
    sucursalesSeleccionadas.length > 0 &&
    cajasSeleccionadas.length === 0
  ) {
    return {
      cajaInicio: null,
      cajaFin: null,
      cajasCodigos: [],
      sinCajasExplicitas: true,
    };
  }

  if (cajasSeleccionadas.length === 0) {
    return { cajaInicio: null, cajaFin: null, cajasCodigos: [] };
  }
  const codigos = codigosFromCajaKeys(cajasSeleccionadas.map(String));
  if (codigos.length === 0) {
    return { cajaInicio: null, cajaFin: null, cajasCodigos: [] };
  }
  if (isContiguous(codigos)) {
    return {
      cajaInicio: codigos[0],
      cajaFin: codigos[codigos.length - 1],
      cajasCodigos: [],
    };
  }
  return { cajaInicio: null, cajaFin: null, cajasCodigos: codigos };
}

/** Combina filtros de sucursal + caja listos para el API y encabezados. */
export function resolveReporteFiltrosCombinado({
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
  cajasTodas = true,
  cajasSeleccionadas = [],
  sucursales = [],
  cajasCatalogo = [],
}) {
  const caja = resolveCajaFiltro({
    cajasTodas,
    cajasSeleccionadas,
    sucursalesTodas,
    sucursalesSeleccionadas,
  });
  const sucursal = resolveSucursalFiltroEfectivo({
    sucursalesTodas,
    sucursalesSeleccionadas,
    cajasTodas,
    cajasSeleccionadas,
    sucursales,
    cajasCatalogo,
  });
  return { sucursal, caja };
}

/** Sucursales a mostrar en encabezado (prioriza la selección explícita de sucursales). */
export function resolveSucursalesEncabezadoSeleccionadas({
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
  cajasTodas = true,
  cajasSeleccionadas = [],
  sucursales = [],
  cajasCatalogo = [],
}) {
  if (sucursalesTodas) {
    const todasEnScope = cajasTodasEnScope(cajasTodas, sucursalesTodas, sucursalesSeleccionadas);
    if (!todasEnScope && cajasSeleccionadas.length > 0) {
      const catalogoEnScope = cajasCatalogoEnAlcanceSucursales(cajasCatalogo, {
        sucursalesTodas,
        sucursalesSeleccionadas,
        sucursales,
      });
      return extractSucursalIdsFromCajaKeys(cajasSeleccionadas, sucursales, catalogoEnScope);
    }
    return sucursales.map((s) => String(s.sucursalId ?? '').trim()).filter(Boolean);
  }
  return sucursalesSeleccionadas;
}

/** Params de sucursal listos para el API de reportes. */
export function buildReporteSucursalParams(sucursalFiltro) {
  if (sucursalFiltro.allBranches) {
    return { allBranches: true };
  }
  if (sucursalFiltro.sucursalIds?.length) {
    return { sucursalIds: sucursalFiltro.sucursalIds.join(',') };
  }
  if (sucursalFiltro.sucursalId) {
    return { sucursalId: sucursalFiltro.sucursalId };
  }
  return {};
}

/** Params de caja listos para el API de reportes. */
export function buildReporteCajaParams(cajaFiltro) {
  if (cajaFiltro.cajasCodigos?.length) {
    return { cajas: cajaFiltro.cajasCodigos.join(',') };
  }
  if (cajaFiltro.cajaInicio != null && cajaFiltro.cajaFin != null) {
    return { cajaInicio: cajaFiltro.cajaInicio, cajaFin: cajaFiltro.cajaFin };
  }
  return {};
}

function buildEncabezadoListaResumen(items = [], { inlineMax = 3, pluralLabel = 'ítems seleccionados' } = {}) {
  const list = items.map((x) => String(x ?? '').trim()).filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) return { texto: list[0], items: list };
  return {
    texto: list.length <= inlineMax ? list.join(' · ') : `${list.length} ${pluralLabel}`,
    items: list,
  };
}

/** Texto e ítems de sucursales para encabezados de reporte. */
export function buildSucursalesEncabezado({
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
  sucursales = [],
}) {
  if (sucursalesTodas) {
    const resumen = buildEncabezadoListaResumen(
      sucursales.map((s) => s.nombreSucursal),
      { inlineMax: 3, pluralLabel: 'sucursales seleccionadas' },
    );
    if (resumen) return resumen;
    return { texto: 'Todas las sucursales', items: [] };
  }
  if (sucursalesSeleccionadas.length === 0) {
    return { texto: 'Todas las sucursales', items: [] };
  }
  const items = sucursalesSeleccionadas
    .map((id) =>
      sucursales.find((s) => String(s.sucursalId) === String(id))?.nombreSucursal?.trim(),
    )
    .filter(Boolean);
  if (items.length === 0) return { texto: '—', items: [] };
  if (items.length === 1) return { texto: items[0], items };
  return {
    texto: items.length <= 3 ? items.join(' · ') : `${items.length} sucursales seleccionadas`,
    items,
  };
}

/** Texto e ítems de cajas para encabezados de reporte. */
export function buildCajasEncabezado({
  cajasTodas = true,
  cajasSeleccionadas = [],
  cajasCatalogo = [],
  sucursales = [],
  sucursalesTodas = true,
  sucursalesSeleccionadas = [],
}) {
  if (cajasTodas) {
    const filtrado = filterCajasCatalogoPorSucursales(cajasCatalogo, {
      sucursalesTodas,
      sucursalesSeleccionadas,
      sucursales,
    });
    const catalogItems = buildCajasAgrupadas(cajasCatalogo, filtrado, {
      sucursalesTodas,
      sucursalesSeleccionadas,
    });
    const resumen = buildEncabezadoListaResumen(
      catalogItems.map((c) => c.label),
      { inlineMax: 2, pluralLabel: 'cajas seleccionadas' },
    );
    if (resumen) return resumen;
    return { texto: 'Todas las cajas', items: [] };
  }
  if (cajasSeleccionadas.length === 0) {
    return { texto: 'Todas las cajas', items: [] };
  }
  const filtrado = filterCajasCatalogoPorSucursales(cajasCatalogo, {
    sucursalesTodas,
    sucursalesSeleccionadas,
    sucursales,
  });
  const catalogItems = buildCajasAgrupadas(cajasCatalogo, filtrado, {
    sucursalesTodas,
    sucursalesSeleccionadas,
  });
  const selected = new Set(cajasSeleccionadas.map(String));
  const items = catalogItems.filter((c) => selected.has(c.key)).map((c) => c.label);
  if (items.length === 0) {
    const codigos = codigosFromCajaKeys(cajasSeleccionadas);
    const rowsByCodigo = groupCajaRowsByCodigo(normalizeCajaRows(filtrado));
    const fallbackItems = codigos.map((codigo) => {
      const grupo = rowsByCodigo.find(([c]) => c === codigo)?.[1] ?? [];
      return buildCajaGrupoLabel(codigo, grupo);
    });
    return {
      texto:
        fallbackItems.length <= 2
          ? fallbackItems.join(' · ')
          : `${fallbackItems.length} cajas seleccionadas`,
      items: fallbackItems,
    };
  }
  return {
    texto: items.length <= 2 ? items.join(' · ') : `${items.length} cajas seleccionadas`,
    items,
  };
}
